import * as React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  toast,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  type CreateSubjectRequest,
  type Subject,
  type SubjectKind,
  type SubjectParallelism,
  type SubjectStream,
  type UpdateSubjectRequest,
} from "@eduviaos/shared";
import { AIDraftButton } from "@eduviaos/capability-ai-ui";
import { useCreateSubject, useUpdateSubject } from "@/hooks/useAcademicCatalogue";
import { draftRow, useAIDraftGenerator, type SubjectDraft } from "@/lib/aiDraft";

/**
 * Phase H4 FE-1 — subject editor sheet.
 *
 * Used in two modes:
 *   - mode="create" → renders blank, posts via {@link useCreateSubject}.
 *   - mode="edit"   → renders prefilled from `existing`, patches via
 *     {@link useUpdateSubject}.
 *
 * Field rationale:
 *   - `code` is canonicalised to upper-case ASCII server-side; we
 *     surface the canonicalisation hint inline so the admin doesn't
 *     get a 422 surprise on a lowercase paste.
 *   - `applicable_grades` is a comma-separated text input — empty list
 *     means "applicable to every grade" per BE convention. Validating
 *     these as a structured picker requires the AcademicYearConfig
 *     capability that lands in a later phase; the BE accepts free-text
 *     today and stamps a 422 only when the eligibility builder wires
 *     the captured grade against a configured roster.
 *
 * The sheet stays open on validation/conflict errors so the admin can
 * correct without losing partially-entered work — same UX contract as
 * the F1 designation editor.
 */

const SUBJECT_KIND_OPTIONS: ReadonlyArray<{ value: SubjectKind; label: string; helper: string }> = [
  { value: "theory", label: "Theory", helper: "Standard classroom subject (Maths, History, …)." },
  { value: "lab", label: "Lab", helper: "Hands-on practical session — requires a lab room." },
  {
    value: "co_curricular",
    label: "Co-curricular",
    helper: "Sports / arts / clubs that don't carry academic weight.",
  },
  {
    value: "language",
    label: "Language",
    helper: "Language paper (Hindi, Sanskrit, French, …).",
  },
  {
    value: "physical_education",
    label: "Physical education",
    helper: "PE / games / health periods.",
  },
  {
    value: "elective",
    label: "Elective",
    helper: "Optional choice subject offered to a sub-set of students.",
  },
];

const SUBJECT_PARALLELISM_OPTIONS: ReadonlyArray<{
  value: SubjectParallelism;
  label: string;
  helper: string;
}> = [
  {
    value: "section",
    label: "Per section",
    helper: "Each section gets its own teacher + slot (the common case).",
  },
  {
    value: "combined",
    label: "Combined",
    helper: "All sections of the grade attend the same period (assembly, library).",
  },
];

const SUBJECT_STREAM_OPTIONS: ReadonlyArray<{
  value: SubjectStream;
  label: string;
}> = [
  { value: "none", label: "None / not stream-specific" },
  { value: "science", label: "Science" },
  { value: "commerce", label: "Commerce" },
  { value: "arts", label: "Arts / Humanities" },
  { value: "vocational", label: "Vocational" },
];

interface DraftState {
  code: string;
  name: string;
  aliases: string;
  kind: SubjectKind;
  default_periods_per_week: number;
  default_period_length_min: number;
  applicable_grades: string;
  requires_lab_room: boolean;
  parallelism: SubjectParallelism;
  stream_tag: SubjectStream;
}

const EMPTY_DRAFT: DraftState = {
  code: "",
  name: "",
  aliases: "",
  kind: "theory",
  default_periods_per_week: 4,
  default_period_length_min: 40,
  applicable_grades: "",
  requires_lab_room: false,
  parallelism: "section",
  stream_tag: "none",
};

function fromExisting(subject: Subject): DraftState {
  return {
    code: subject.code,
    name: subject.name,
    aliases: subject.aliases.join(", "),
    kind: subject.kind,
    default_periods_per_week: subject.default_periods_per_week,
    default_period_length_min: subject.default_period_length_min,
    applicable_grades: subject.applicable_grades.join(", "),
    requires_lab_room: subject.requires_lab_room,
    parallelism: subject.parallelism,
    stream_tag: subject.stream_tag,
  };
}

function optionLabel(
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string | null,
): string | null {
  if (!value) return null;
  return options.find((o) => o.value === value)?.label ?? value;
}

function describeSubjectDraft(draft: SubjectDraft) {
  return [
    ...draftRow("Code", draft.code),
    ...draftRow("Name", draft.name),
    ...draftRow("Kind", optionLabel(SUBJECT_KIND_OPTIONS, draft.subject_kind)),
    ...draftRow("Periods per week", draft.default_periods_per_week),
    ...draftRow(
      "Period length",
      draft.default_period_length_min === null ? null : `${draft.default_period_length_min} min`,
    ),
    ...draftRow("Grades", draft.applicable_grades.join(", ")),
    ...draftRow("Needs a lab room", draft.requires_lab_room ? "Yes" : null),
    ...draftRow("Stream", optionLabel(SUBJECT_STREAM_OPTIONS, draft.stream_tag)),
  ];
}

function mergeSubjectDraft(prev: DraftState, draft: SubjectDraft): DraftState {
  return {
    ...prev,
    code: draft.code ?? prev.code,
    name: draft.name ?? prev.name,
    kind: (draft.subject_kind as SubjectKind | null) ?? prev.kind,
    default_periods_per_week: draft.default_periods_per_week ?? prev.default_periods_per_week,
    default_period_length_min: draft.default_period_length_min ?? prev.default_period_length_min,
    applicable_grades:
      draft.applicable_grades.length > 0
        ? draft.applicable_grades.join(", ")
        : prev.applicable_grades,
    requires_lab_room: draft.requires_lab_room ?? prev.requires_lab_room,
    stream_tag: (draft.stream_tag as SubjectStream | null) ?? prev.stream_tag,
  };
}

function splitCsv(value: string): string[] {
  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

interface SubjectEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing: Subject | null;
  onSaved?: (saved: Subject) => void;
}

export function SubjectEditorSheet({
  open,
  onOpenChange,
  existing,
  onSaved,
}: SubjectEditorSheetProps) {
  const [draft, setDraft] = React.useState<DraftState>(EMPTY_DRAFT);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const createMutation = useCreateSubject();
  const updateMutation = useUpdateSubject();

  const isEdit = existing !== null;
  const isPending = createMutation.isPending || updateMutation.isPending;
  const generateSubjectDraft = useAIDraftGenerator("subject");

  // Re-seed the draft whenever the sheet opens. We deliberately do NOT
  // reset on close — the sheet remounts on the next open via the
  // `existing` prop change, so the user gets a clean form every time
  // without us having to flush state mid-animation.
  React.useEffect(() => {
    if (!open) return;
    setDraft(existing ? fromExisting(existing) : EMPTY_DRAFT);
    setErrorMessage(null);
  }, [open, existing]);

  const handleSave = async () => {
    setErrorMessage(null);
    const code = draft.code.trim().toUpperCase();
    const name = draft.name.trim();
    if (!isEdit && !code) {
      setErrorMessage("Subject code is required.");
      return;
    }
    if (!name) {
      setErrorMessage("Subject name is required.");
      return;
    }

    try {
      if (isEdit && existing) {
        const body: UpdateSubjectRequest = {
          name,
          aliases: splitCsv(draft.aliases),
          kind: draft.kind,
          default_periods_per_week: draft.default_periods_per_week,
          default_period_length_min: draft.default_period_length_min,
          applicable_grades: splitCsv(draft.applicable_grades),
          requires_lab_room: draft.requires_lab_room,
          parallelism: draft.parallelism,
          stream_tag: draft.stream_tag,
        };
        const saved = await updateMutation.mutateAsync({ id: existing.id, body });
        onSaved?.(saved);
        toast.success(`Saved changes to "${saved.name}".`);
        onOpenChange(false);
      } else {
        const body: CreateSubjectRequest = {
          code,
          name,
          aliases: splitCsv(draft.aliases),
          kind: draft.kind,
          default_periods_per_week: draft.default_periods_per_week,
          default_period_length_min: draft.default_period_length_min,
          applicable_grades: splitCsv(draft.applicable_grades),
          requires_lab_room: draft.requires_lab_room,
          parallelism: draft.parallelism,
          stream_tag: draft.stream_tag,
        };
        const saved = await createMutation.mutateAsync(body);
        onSaved?.(saved);
        toast.success(`Created "${saved.name}".`);
        onOpenChange(false);
      }
    } catch (raw) {
      if (raw instanceof ApiError) {
        if (raw.code === "CONFLICT") {
          setErrorMessage(
            `A subject with code "${code}" already exists. Pick a different code or edit the existing row.`,
          );
          return;
        }
        if (raw.message) {
          setErrorMessage(raw.message);
          return;
        }
      }
      setErrorMessage(
        raw instanceof Error && raw.message
          ? raw.message
          : "Could not save the subject — please try again.",
      );
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full max-w-xl flex-col gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="border-border/60 border-b px-6 py-4">
          <SheetTitle>{isEdit ? `Edit subject: ${existing?.name}` : "New subject"}</SheetTitle>
          <SheetDescription>
            Subjects are tenant-wide. Codes are canonicalised to upper-case ASCII (e.g.{" "}
            <code className="text-foreground">MATH</code>,{" "}
            <code className="text-foreground">PHY-LAB</code>) and must be unique within your
            institution.
          </SheetDescription>
          {isEdit ? null : (
            <div className="pt-2">
              <AIDraftButton<SubjectDraft>
                title="Draft a subject"
                placeholder="e.g. Physics practicals for grades 11 and 12, science stream, two double periods a week"
                generate={generateSubjectDraft}
                describe={describeSubjectDraft}
                onApply={(next) => {
                  setDraft((prev) => mergeSubjectDraft(prev, next));
                  setErrorMessage(null);
                }}
                disabled={isPending}
                data-testid="subject-ai-draft"
              />
            </div>
          )}
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
          {errorMessage ? (
            <div
              role="alert"
              className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
            >
              <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span className="flex-1">{errorMessage}</span>
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="subject-code">Code</Label>
              <Input
                id="subject-code"
                value={draft.code}
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, code: event.target.value.toUpperCase() }))
                }
                placeholder="MATH"
                disabled={isEdit}
                aria-describedby="subject-code-help"
                maxLength={24}
              />
              <p id="subject-code-help" className="text-muted-foreground text-[11px] leading-snug">
                {isEdit
                  ? "Code is immutable after creation — it's referenced by eligibility rows."
                  : "ASCII letters, digits, hyphens, underscores. Auto-uppercased."}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="subject-name">Display name</Label>
              <Input
                id="subject-name"
                value={draft.name}
                onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Mathematics"
                maxLength={160}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="subject-aliases">Aliases</Label>
            <Input
              id="subject-aliases"
              value={draft.aliases}
              onChange={(event) => setDraft((prev) => ({ ...prev, aliases: event.target.value }))}
              placeholder="Math, Maths"
            />
            <p className="text-muted-foreground text-[11px] leading-snug">
              Comma-separated alternate spellings the CSV importer + autocomplete recognise.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="subject-kind">Kind</Label>
              <Select
                value={draft.kind}
                onValueChange={(value) =>
                  setDraft((prev) => ({ ...prev, kind: value as SubjectKind }))
                }
              >
                <SelectTrigger id="subject-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUBJECT_KIND_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex flex-col">
                        <span>{option.label}</span>
                        <span className="text-muted-foreground text-[11px]">{option.helper}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="subject-stream">Stream tag</Label>
              <Select
                value={draft.stream_tag}
                onValueChange={(value) =>
                  setDraft((prev) => ({ ...prev, stream_tag: value as SubjectStream }))
                }
              >
                <SelectTrigger id="subject-stream">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUBJECT_STREAM_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="subject-periods">Default periods / week</Label>
              <Input
                id="subject-periods"
                type="number"
                min={0}
                max={40}
                value={draft.default_periods_per_week}
                onChange={(event) =>
                  setDraft((prev) => ({
                    ...prev,
                    default_periods_per_week: clampInt(event.target.value, 0, 40, 4),
                  }))
                }
              />
              <p className="text-muted-foreground text-[11px] leading-snug">
                Solver default. Admin can override per generation run.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="subject-period-length">Period length (min)</Label>
              <Input
                id="subject-period-length"
                type="number"
                min={10}
                max={240}
                value={draft.default_period_length_min}
                onChange={(event) =>
                  setDraft((prev) => ({
                    ...prev,
                    default_period_length_min: clampInt(event.target.value, 10, 240, 40),
                  }))
                }
              />
              <p className="text-muted-foreground text-[11px] leading-snug">
                CBSE default is 40. Override if your grid uses longer / shorter periods.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="subject-grades">Applicable grades</Label>
            <Input
              id="subject-grades"
              value={draft.applicable_grades}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, applicable_grades: event.target.value }))
              }
              placeholder="9, 10, 11"
            />
            <p className="text-muted-foreground text-[11px] leading-snug">
              Comma-separated grade keys. Leave blank to mark this subject applicable to every grade
              in your institution.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="subject-parallelism">Parallelism</Label>
            <Select
              value={draft.parallelism}
              onValueChange={(value) =>
                setDraft((prev) => ({ ...prev, parallelism: value as SubjectParallelism }))
              }
            >
              <SelectTrigger id="subject-parallelism">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUBJECT_PARALLELISM_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex flex-col">
                      <span>{option.label}</span>
                      <span className="text-muted-foreground text-[11px]">{option.helper}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border-border/60 bg-muted/40 flex items-start gap-3 rounded-md border px-3 py-2.5">
            <Checkbox
              id="subject-lab-room"
              checked={draft.requires_lab_room}
              onCheckedChange={(value) =>
                setDraft((prev) => ({ ...prev, requires_lab_room: value === true }))
              }
            />
            <div className="space-y-0.5">
              <Label htmlFor="subject-lab-room" className="text-sm font-medium">
                Requires a lab room
              </Label>
              <p className="text-muted-foreground text-[11px] leading-snug">
                Hard constraint for the timetable solver — slots for this subject won't be placed
                without an available lab room.
              </p>
            </div>
          </div>
        </div>

        <div className="border-border/60 flex items-center justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                Saving…
              </>
            ) : isEdit ? (
              "Save changes"
            ) : (
              "Create subject"
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function clampInt(raw: string, min: number, max: number, fallback: number): number {
  const parsed = Number.parseInt(raw, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.min(Math.max(parsed, min), max);
}
